import React from 'react';
import { useCartQuery } from '@/api/useCartQuery';
import { useUpdateCart } from '@/api/useUpdateCart';
import { cn } from '@bem-react/classname';
import { InputPicker } from 'rsuite';
import './CurrencySelector.module.scss';

export type Currency = {
  id: number | string;
  iso: string; // e.g., "USD", "NOK"
  name: string; // Human readable name, e.g., "US Dollar"
};

export type CurrencySelectorProps = {
  currencies: Currency[];
  showTitle?: boolean;
  className?: string;
};

export default function CurrencySelector({ currencies, showTitle = true, className = '' }: CurrencySelectorProps) {
  const { data: cart } = useCartQuery();
  const updateCart = useUpdateCart();

  // BEM block
  const cnCur = cn('cCurrencySelector');

  // Current currency is stored as ISO on the cart (cart.paymentCurrency)
  const currentIso = cart?.paymentCurrency;
  const current = currencies.find((c) => c.iso === currentIso);

  const onChange = (value: string | null) => {
    const selectedId = value ?? '';
    if (!selectedId) return;
    updateCart.mutate({ paymentCurrency: selectedId });
  };

  return (
    <div className={cnCur()}>
      {showTitle && (
        <div className={cnCur('Title')}>Currency</div>
      )}

      <div className={cnCur('Wrapper')}>
        <label className={cnCur('Label')} htmlFor="paymentCurrency">Currency</label>
        <InputPicker
          id="paymentCurrency"
          data={currencies.map((c) => ({ label: `${c.name} (${c.iso})`, value: String(c.id) }))}
          value={current ? String(current.id) : undefined}
          onChange={onChange}
          placeholder={current ? `${current.name} (${current.iso})` : 'Select currency'}
          disabled={updateCart.isPending}
          loading={updateCart.isPending}
          className={cnCur('Select')}
          cleanable={false}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
}
